import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import SafeIcon from '../../common/SafeIcon';
import MoneyInput from '../../common/MoneyInput';
import * as FiIcons from 'react-icons/fi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const { FiBookOpen, FiCalendar } = FiIcons;

const EducationCalculator = () => {
  const { user } = useAuth();
  const { saveCalculation, personalInfo } = useData();
  const currency = personalInfo?.investment_currency || 'Currency';

  const [formData, setFormData] = useState({
    currentAge: '',
    collegeAge: '18',
    currentCost: '50000',
    inflationRate: '5',
    currentSavings: '',
    monthlyContribution: '',
    expectedReturn: '6',
    yearsInCollege: '4'
  });

  const [results, setResults] = useState(null);

  const calculateEducation = () => {
    const { currentAge, collegeAge, currentCost, inflationRate, currentSavings, monthlyContribution, expectedReturn, yearsInCollege } = formData;
    const yearsToCollege = parseInt(collegeAge) - parseInt(currentAge);
    const monthsToCollege = yearsToCollege * 12;

    const futureCostPerYear = parseFloat(currentCost) * Math.pow(1 + parseFloat(inflationRate) / 100, yearsToCollege);
    const totalEducationCost = futureCostPerYear * parseInt(yearsInCollege);

    const monthlyReturn = parseFloat(expectedReturn) / 100 / 12;
    const futureValueSavings = parseFloat(currentSavings) * Math.pow(1 + monthlyReturn, monthsToCollege);
    const futureValueContributions = parseFloat(monthlyContribution) * ((Math.pow(1 + monthlyReturn, monthsToCollege) - 1) / monthlyReturn);
    const totalSavings = futureValueSavings + futureValueContributions;

    const shortfall = Math.max(0, totalEducationCost - totalSavings);
    const surplus = Math.max(0, totalSavings - totalEducationCost);
    const requiredMonthlyContribution = monthsToCollege > 0 ? (totalEducationCost - futureValueSavings) * monthlyReturn / (Math.pow(1 + monthlyReturn, monthsToCollege) - 1) : 0;

    const chartData = [];
    for (let year = 0; year <= yearsToCollege; year++) {
      const months = year * 12;
      const savingsValue = parseFloat(currentSavings) * Math.pow(1 + monthlyReturn, months);
      const contributionsValue = months > 0 ? parseFloat(monthlyContribution) * ((Math.pow(1 + monthlyReturn, months) - 1) / monthlyReturn) : 0;
      const educationCost = parseFloat(currentCost) * Math.pow(1 + parseFloat(inflationRate) / 100, year);
      chartData.push({ year: parseInt(currentAge) + year, savings: Math.round(savingsValue + contributionsValue), cost: Math.round(educationCost * parseInt(yearsInCollege)) });
    }

    const costBreakdown = [];
    for (let year = 1; year <= parseInt(yearsInCollege); year++) {
      const yearCost = futureCostPerYear * Math.pow(1 + parseFloat(inflationRate) / 100, year - 1);
      costBreakdown.push({ year: `Year ${year}`, cost: Math.round(yearCost) });
    }

    const calculationResults = { totalEducationCost: Math.round(totalEducationCost), futureCostPerYear: Math.round(futureCostPerYear), totalSavings: Math.round(totalSavings), shortfall: Math.round(shortfall), surplus: Math.round(surplus), requiredMonthlyContribution: Math.round(requiredMonthlyContribution), chartData, costBreakdown, inputs: formData };
    setResults(calculationResults);

    if (user) {
      saveCalculation({ type: 'education', inputs: formData, results: calculationResults });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    calculateEducation();
  };

  return (
    <div className="min-h-screen bg-black py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <SafeIcon icon={FiBookOpen} className="w-12 h-12 text-primary-500" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Education Planning</h1>
          <p className="text-xl text-secondary-300 max-w-3xl mx-auto">
            Plan and save for education expenses with professional-grade analytics.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="bg-secondary-900 rounded-xl shadow-lg p-8 border border-primary-500/20">
            <h2 className="text-2xl font-bold text-white mb-6">Education Details</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Child's Current Age</label>
                  <div className="relative">
                    <input type="number" name="currentAge" value={formData.currentAge} onChange={handleChange} className="w-full px-4 py-3 pl-10 border border-secondary-700 bg-secondary-800 rounded-lg text-white focus:ring-2 focus:ring-primary-500 outline-none" placeholder="5" required />
                    <SafeIcon icon={FiCalendar} className="absolute left-3 top-3.5 w-5 h-5 text-primary-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-2">College Starting Age</label>
                  <div className="relative">
                    <input type="number" name="collegeAge" value={formData.collegeAge} onChange={handleChange} className="w-full px-4 py-3 pl-10 border border-secondary-700 bg-secondary-800 rounded-lg text-white focus:ring-2 focus:ring-primary-500 outline-none" placeholder="18" />
                    <SafeIcon icon={FiCalendar} className="absolute left-3 top-3.5 w-5 h-5 text-primary-500" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <MoneyInput
                  label="Current Annual Cost"
                  name="currentCost"
                  value={formData.currentCost}
                  onChange={handleChange}
                  currency={currency}
                />
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Years in College</label>
                  <select name="yearsInCollege" value={formData.yearsInCollege} onChange={handleChange} className="w-full px-4 py-3 border border-secondary-700 bg-secondary-800 rounded-lg text-white focus:ring-2 focus:ring-primary-500 outline-none appearance-none">
                    <option value="2">2 years</option>
                    <option value="3">3 years</option>
                    <option value="4">4 years</option>
                    <option value="5">5 years</option>
                    <option value="6">6 years</option>
                  </select>
                </div>
              </div>

              <MoneyInput
                label="Current Education Savings"
                name="currentSavings"
                value={formData.currentSavings}
                onChange={handleChange}
                currency={currency}
                required
              />

              <MoneyInput
                label="Monthly Contribution"
                name="monthlyContribution"
                value={formData.monthlyContribution}
                onChange={handleChange}
                currency={currency}
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Inflation Rate (%)</label>
                  <input type="number" step="0.1" name="inflationRate" value={formData.inflationRate} onChange={handleChange} className="w-full px-4 py-3 border border-secondary-700 bg-secondary-800 rounded-lg text-white focus:ring-2 focus:ring-primary-500 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Expected Return (%)</label>
                  <input type="number" step="0.1" name="expectedReturn" value={formData.expectedReturn} onChange={handleChange} className="w-full px-4 py-3 border border-secondary-700 bg-secondary-800 rounded-lg text-white focus:ring-2 focus:ring-primary-500 outline-none" />
                </div>
              </div>

              <button type="submit" className="w-full bg-primary-500 hover:bg-primary-400 text-black px-6 py-4 rounded-xl font-bold transition-all shadow-lg shadow-primary-500/20">
                Calculate Education Plan
              </button>
            </form>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
            {results ? (
              <div className="bg-secondary-900 rounded-xl p-8 border border-primary-500/20">
                <h3 className="text-xl font-bold text-white mb-6">Cost Projection</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-black p-4 rounded-xl border border-primary-500/10">
                    <h4 className="text-xs font-black text-secondary-500 uppercase tracking-widest mb-2">Total Cost</h4>
                    <p className="text-2xl font-bold text-primary-500">{currency} {results.totalEducationCost.toLocaleString()}</p>
                  </div>
                  <div className="bg-black p-4 rounded-xl border border-primary-500/10">
                    <h4 className="text-xs font-black text-secondary-500 uppercase tracking-widest mb-2">Total Savings</h4>
                    <p className="text-2xl font-bold text-primary-500">{currency} {results.totalSavings.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-secondary-900 rounded-xl p-12 text-center border border-primary-500/10 h-full flex flex-col justify-center">
                <SafeIcon icon={FiBookOpen} className="w-16 h-16 text-secondary-700 mx-auto mb-6" />
                <h3 className="text-xl font-bold text-white mb-2">Plan for Success</h3>
                <p className="text-secondary-400">Fill out the form to see your education savings projection.</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default EducationCalculator;